import {
  applyNodeChanges,
  Background,
  BackgroundVariant,
  ConnectionMode,
  Controls,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  SelectionMode,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
  type NodeChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Grip, Hand, MousePointer2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDiagramViewStore } from '../../store/useDiagramViewStore';
import { useArchitectureStore, SERVICE_NODE_WIDTH, SERVICE_NODE_HEIGHT } from '../../store/useArchitectureStore';
import { useThemeStore } from '../../store/useThemeStore';
import { getCatalogEntry } from '../../lib/architectureNodeCatalog';
import { connectorSteps, nodeRef, nodeSteps, type Step } from '../../lib/explanationRefs';
import ExplanationContextMenu from '../workspace/ExplanationContextMenu';
import TourOverlay from './TourOverlay';
import FlowEdge from './FlowEdge';
import ServiceGroupNode from './ServiceGroupNode';
import ServiceNode from './ServiceNode';

const CANVAS_BG = { light: '#f5f7fb', dark: '#070b14' };

/** Opaque colour of a group's tinted fill over the canvas, matching ServiceGroupNode's translucent background. */
function groupBackdrop(groupColor: string, theme: 'light' | 'dark'): string {
  const channels = (hex: string) => {
    const v = parseInt(hex.replace('#', '').padEnd(6, '0').slice(0, 6), 16);
    return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
  };
  const alpha = theme === 'dark' ? 0x3d / 255 : 0x26 / 255;
  const base = channels(CANVAS_BG[theme]);
  const tint = channels(groupColor);
  return `rgb(${tint.map((c, i) => Math.round(base[i] + (c - base[i]) * alpha)).join(', ')})`;
}

const nodeTypes = { service: ServiceNode, serviceGroup: ServiceGroupNode };
const edgeTypes = { flow: FlowEdge };

// Must match GROUP_PADDING/GROUP_HEADER_HEIGHT in diagram-engine's nestedLayout.ts.
const GROUP_PADDING = 28;
const GROUP_HEADER_HEIGHT = 32;

function InteractionModeToggle() {
  const interactionMode = useDiagramViewStore((s) => s.interactionMode);
  const setInteractionMode = useDiagramViewStore((s) => s.setInteractionMode);
  const showGrid = useDiagramViewStore((s) => s.showGrid);
  const toggleGrid = useDiagramViewStore((s) => s.toggleGrid);

  return (
    <Panel position="top-left">
      <div className="flex items-center gap-0.5 rounded-lg border border-black/10 bg-panel p-1 shadow-md dark:border-white/10 dark:bg-gray-800">
        <button
          onClick={() => setInteractionMode('select')}
          title="Select mode: drag an element to move it, drag empty canvas to pan"
          className={`rounded-md p-1.5 ${
            interactionMode === 'select' ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300' : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
          }`}
        >
          <MousePointer2 className="h-4 w-4" />
        </button>
        <button
          onClick={() => setInteractionMode('pan')}
          title="Pan mode: drag anywhere to move the canvas"
          className={`rounded-md p-1.5 ${
            interactionMode === 'pan' ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300' : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
          }`}
        >
          <Hand className="h-4 w-4" />
        </button>
        <div className="mx-0.5 h-5 w-px bg-gray-200 dark:bg-white/10" />
        <button
          onClick={toggleGrid}
          title={showGrid ? 'Hide dot grid' : 'Show dot grid'}
          className={`rounded-md p-1.5 ${
            showGrid ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300' : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
          }`}
        >
          <Grip className="h-4 w-4" />
        </button>
      </div>
    </Panel>
  );
}

function ToastPanel() {
  const toast = useDiagramViewStore((s) => s.toast);
  if (!toast) return null;
  return (
    <Panel position="top-center">
      <div className="rounded-md bg-gray-900/90 px-3 py-1.5 text-xs font-medium text-white shadow-lg dark:bg-gray-100/90 dark:text-gray-900">
        {toast}
      </div>
    </Panel>
  );
}

function FlowInner({ containerRef }: { containerRef: React.RefObject<HTMLDivElement> }) {
  const nodesData = useArchitectureStore((s) => s.diagram.nodes);
  const edgesData = useArchitectureStore((s) => s.diagram.edges);
  const selection = useArchitectureStore((s) => s.selection);
  const setSelection = useArchitectureStore((s) => s.setSelection);
  const setNodePosition = useArchitectureStore((s) => s.setNodePosition);
  const setNodePositions = useArchitectureStore((s) => s.setNodePositions);
  const setNodeParent = useArchitectureStore((s) => s.setNodeParent);
  const addEdge = useArchitectureStore((s) => s.addEdge);
  const autoLayout = useArchitectureStore((s) => s.autoLayout);
  const reactFlow = useReactFlow();
  const setInstance = useDiagramViewStore((s) => s.setInstance);
  const interactionMode = useDiagramViewStore((s) => s.interactionMode);
  const showToast = useDiagramViewStore((s) => s.showToast);
  const multiSelectedIds = useDiagramViewStore((s) => s.multiSelectedIds);
  const setMultiSelectedIds = useDiagramViewStore((s) => s.setMultiSelectedIds);
  const multiSet = useMemo(() => new Set(multiSelectedIds), [multiSelectedIds]);
  const forceRenderAll = useDiagramViewStore((s) => s.forceRenderAll);
  const showGrid = useDiagramViewStore((s) => s.showGrid);
  const readOnly = useDiagramViewStore((s) => s.readOnly);
  const documentation = useArchitectureStore((s) => s.documentation);
  const tourOpen = useDiagramViewStore((s) => s.tourOpen);
  const tourFocus = useDiagramViewStore((s) => s.tourFocus);
  const [menu, setMenu] = useState<{ x: number; y: number; title: string; steps: Step[] } | null>(null);
  const theme = useThemeStore((s) => s.theme);
  const didAutoLayout = useRef(false);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    if (didAutoLayout.current) return;
    if (nodesData.length > 0 && nodesData.some((n) => !n.position)) {
      didAutoLayout.current = true;
      autoLayout();
    }
  }, [nodesData, autoLayout]);

  const selectedNodeId = selection?.type === 'node' ? selection.id : null;
  const selectedNode = useMemo(() => nodesData.find((n) => n.id === selectedNodeId) ?? null, [nodesData, selectedNodeId]);

  const connectedEdgeIds = useMemo(() => {
    if (!selectedNodeId) return null;
    const memberIds = new Set([selectedNodeId]);
    if (selectedNode?.mode === 'group') {
      for (const n of nodesData) if (n.parentId === selectedNodeId) memberIds.add(n.id);
    }
    const ids = new Set<string>();
    for (const edge of edgesData) {
      if (memberIds.has(edge.fromNodeId) || memberIds.has(edge.toNodeId)) ids.add(edge.id);
    }
    return ids;
  }, [edgesData, selectedNodeId, selectedNode, nodesData]);

  const groupNodes: Node[] = useMemo(
    () =>
      nodesData
        .filter((n) => n.mode === 'group')
        .map((group) => {
          const members = nodesData.filter((n) => n.parentId === group.id && n.position);
          let position: { x: number; y: number };
          let width: number;
          let height: number;
          if (members.length) {
            const minX = Math.min(...members.map((m) => m.position!.x));
            const minY = Math.min(...members.map((m) => m.position!.y));
            const maxX = Math.max(...members.map((m) => m.position!.x + SERVICE_NODE_WIDTH));
            const maxY = Math.max(...members.map((m) => m.position!.y + SERVICE_NODE_HEIGHT));
            position = { x: minX - GROUP_PADDING, y: minY - GROUP_PADDING - GROUP_HEADER_HEIGHT };
            width = maxX - minX + GROUP_PADDING * 2;
            height = maxY - minY + GROUP_PADDING * 2 + GROUP_HEADER_HEIGHT;
          } else {
            position = group.position ?? { x: 0, y: 0 };
            width = SERVICE_NODE_WIDTH;
            height = SERVICE_NODE_HEIGHT + GROUP_HEADER_HEIGHT;
          }
          const node: Node = {
            id: group.id,
            type: 'serviceGroup',
            position,
            style: { width, height },
            data: {
              kind: group.kind,
              label: group.label,
              color: group.color,
              isSelected: selectedNodeId === group.id || multiSet.has(group.id),
              memberCount: members.length,
              tour: tourFocus ? (tourFocus.nodeIds.has(group.id) ? 'focus' : 'dim') : null,
            },
            selected: selectedNodeId === group.id || multiSet.has(group.id),
            selectable: true,
            zIndex: -1,
          };
          return node;
        }),
    [nodesData, selectedNodeId, multiSet, tourFocus],
  );

  const componentNodes: Node[] = useMemo(
    () =>
      nodesData
        .filter((n) => n.mode !== 'group')
        .map((node) => {
          // Nodes inside a group sit on its tinted fill, so their opaque backing must match that, not the bare canvas.
          const parent = node.parentId ? nodesData.find((n) => n.id === node.parentId) : undefined;
          const backdrop = parent ? groupBackdrop(parent.color ?? getCatalogEntry(parent.kind)?.color ?? '#6366f1', theme) : undefined;
          return {
          id: node.id,
          type: 'service',
          position: node.position ?? { x: 0, y: 0 },
          style: { width: SERVICE_NODE_WIDTH, height: SERVICE_NODE_HEIGHT },
          data: {
            kind: node.kind,
            label: node.label,
            color: node.color,
            isSelected: selectedNodeId === node.id || multiSet.has(node.id),
            backdrop,
            tour: tourFocus ? (tourFocus.nodeIds.has(node.id) ? 'focus' : 'dim') : null,
          },
          selected: selectedNodeId === node.id || multiSet.has(node.id),
          zIndex: 1,
          };
        }),
    [nodesData, selectedNodeId, multiSet, theme, tourFocus],
  );

  const computedNodes = useMemo(() => [...groupNodes, ...componentNodes], [groupNodes, componentNodes]);

  const [renderNodes, setRenderNodes] = useState<Node[]>(computedNodes);
  useEffect(() => {
    if (isDraggingRef.current) return;
    setRenderNodes(computedNodes);
  }, [computedNodes]);

  // Dragging a group node must carry its member nodes along with it, exactly like
  // dragging a domain group in the DB diagram carries its member tables.
  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setRenderNodes((nds) => {
        let next = applyNodeChanges(changes, nds);
        for (const change of changes) {
          if (change.type !== 'position' || !change.position) continue;
          const prevNode = nds.find((n) => n.id === change.id);
          if (!prevNode || prevNode.type !== 'serviceGroup') continue;
          const dx = change.position.x - prevNode.position.x;
          const dy = change.position.y - prevNode.position.y;
          if (!dx && !dy) continue;
          const movedByChange = new Set(changes.filter((c) => c.type === 'position').map((c) => (c as { id: string }).id));
          const memberIds = new Set(nodesData.filter((n) => n.parentId === change.id && !movedByChange.has(n.id)).map((n) => n.id));
          next = next.map((n) => (memberIds.has(n.id) ? { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } } : n));
        }
        return next;
      });
    },
    [nodesData],
  );

  const renderNodesRef = useRef(renderNodes);
  useEffect(() => {
    renderNodesRef.current = renderNodes;
  }, [renderNodes]);

  const edges: Edge[] = useMemo(
    () =>
      edgesData.map((edge) => ({
        id: edge.id,
        type: 'flow',
        source: edge.fromNodeId,
        target: edge.toNodeId,
        sourceHandle: edge.fromHandle,
        targetHandle: edge.toHandle,
        data: {
          label: edge.label,
          direction: edge.direction,
          animated: edge.animated,
          speed: edge.speed,
          highlighted: connectedEdgeIds?.has(edge.id) ?? false,
          tour: tourFocus ? (tourFocus.edgeIds.has(edge.id) ? 'focus' : 'dim') : null,
        },
        selected: selection?.type === 'edge' && selection.id === edge.id,
        zIndex: connectedEdgeIds?.has(edge.id) ? 10 : 0,
      })),
    [edgesData, selection, connectedEdgeIds, tourFocus],
  );

  useEffect(() => {
    if (selection?.type !== 'node') return;
    const node = nodesData.find((n) => n.id === selection.id);
    if (!node?.position) return;
    if (node.mode === 'group') {
      // Bring the whole group (the box drawn around its members) into view.
      if (reactFlow.getNode(node.id)) {
        reactFlow.fitView({ nodes: [{ id: node.id }], padding: 0.2, maxZoom: 1, duration: 400 });
        return;
      }
    }
    reactFlow.setCenter(node.position.x + SERVICE_NODE_WIDTH / 2, node.position.y + SERVICE_NODE_HEIGHT / 2, {
      zoom: Math.max(reactFlow.getZoom(), 0.75),
      duration: 300,
    });
  }, [selection, nodesData, reactFlow]);

  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return;
      addEdge(connection.source, connection.target, connection.sourceHandle, connection.targetHandle);
    },
    [addEdge],
  );

  const blockedInPanMode = useCallback(() => {
    showToast('Switch to Select mode to select elements');
  }, [showToast]);

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      if (interactionMode === 'pan') {
        blockedInPanMode();
        return;
      }
      setSelection({ type: 'node', id: node.id });
    },
    [interactionMode, blockedInPanMode, setSelection],
  );

  const handleEdgeClick = useCallback(
    (_: React.MouseEvent, edge: Edge) => {
      if (interactionMode === 'pan') {
        blockedInPanMode();
        return;
      }
      setSelection({ type: 'edge', id: edge.id });
    },
    [interactionMode, blockedInPanMode, setSelection],
  );

  // Right-click on a node, a group or a connector offers "Go to explanation".
  const handleNodeContextMenu = useCallback(
    (e: React.MouseEvent, node: Node) => {
      e.preventDefault();
      const target = nodesData.find((n) => n.id === node.id);
      if (!target) return;
      setMenu({ x: e.clientX, y: e.clientY, title: target.label, steps: nodeSteps(nodeRef(nodesData, target)) });
    },
    [nodesData],
  );

  const handleEdgeContextMenu = useCallback(
    (e: React.MouseEvent, edge: Edge) => {
      e.preventDefault();
      const from = nodesData.find((n) => n.id === edge.source);
      const to = nodesData.find((n) => n.id === edge.target);
      if (!from || !to) return;
      setMenu({
        x: e.clientX,
        y: e.clientY,
        title: `Connection ${from.label} → ${to.label}`,
        steps: connectorSteps(nodeRef(nodesData, from), nodeRef(nodesData, to)),
      });
    },
    [nodesData],
  );

  const handlePaneClick = useCallback(() => {
    setSelection(null);
    setMultiSelectedIds([]);
  }, [setSelection, setMultiSelectedIds]);

  // Marquee (or ctrl/cmd-click) selection: several nodes are highlighted and moved/deleted together, while a
  // single picked node behaves like a normal click and feeds the properties panel.
  const handleSelectionChange = useCallback(
    ({ nodes: picked }: { nodes: Node[] }) => {
      if (picked.length > 1) {
        setMultiSelectedIds(picked.map((n) => n.id));
        if (useArchitectureStore.getState().selection) setSelection(null);
        return;
      }
      setMultiSelectedIds([]);
      const only = picked[0];
      if (!only) return;
      const current = useArchitectureStore.getState().selection;
      if (!(current?.type === 'node' && current.id === only.id)) setSelection({ type: 'node', id: only.id });
    },
    [setSelection, setMultiSelectedIds],
  );

  const handleNodeDragStart = useCallback(() => {
    isDraggingRef.current = true;
  }, []);

  const handleNodeDragStop = useCallback(
    (_: MouseEvent | TouchEvent, node: Node, dragged: Node[]) => {
      isDraggingRef.current = false;

      if (dragged.length > 1) {
        const updates: Record<string, { x: number; y: number }> = {};
        const draggedIds = new Set(dragged.map((d) => d.id));
        for (const rn of renderNodesRef.current) {
          if (rn.type === 'service' && (rn.selected || draggedIds.has(rn.id))) updates[rn.id] = rn.position;
        }
        for (const d of dragged) {
          if (d.type !== 'serviceGroup') continue;
          for (const m of nodesData) {
            if (m.parentId !== d.id) continue;
            const rn = renderNodesRef.current.find((n) => n.id === m.id);
            if (rn) updates[m.id] = rn.position;
          }
        }
        if (Object.keys(updates).length) setNodePositions(updates, true);
        return;
      }

      if (node.type === 'serviceGroup') {
        const memberIds = new Set(nodesData.filter((n) => n.parentId === node.id).map((n) => n.id));
        const updates: Record<string, { x: number; y: number }> = {};
        for (const rn of renderNodesRef.current) {
          if (memberIds.has(rn.id)) updates[rn.id] = rn.position;
        }
        if (Object.keys(updates).length) setNodePositions(updates, true);
        return;
      }

      setNodePosition(node.id, node.position, true);

      // Drag-to-nest: if the node's center now lands inside a group node's box, make it
      // a member of that group; if it was a member and got dragged out, ungroup it.
      const centerX = node.position.x + SERVICE_NODE_WIDTH / 2;
      const centerY = node.position.y + SERVICE_NODE_HEIGHT / 2;
      const hit = renderNodesRef.current.find((n) => {
        if (n.type !== 'serviceGroup' || n.id === node.id) return false;
        const width = (n.style?.width as number) ?? SERVICE_NODE_WIDTH;
        const height = (n.style?.height as number) ?? SERVICE_NODE_HEIGHT;
        return (
          centerX >= n.position.x &&
          centerX <= n.position.x + width &&
          centerY >= n.position.y &&
          centerY <= n.position.y + height
        );
      });
      const currentParentId = nodesData.find((n) => n.id === node.id)?.parentId ?? null;
      const newParentId = hit?.id ?? null;
      if (newParentId !== currentParentId) setNodeParent(node.id, newParentId);
    },
    [nodesData, setNodePosition, setNodePositions, setNodeParent],
  );

  return (
    <div ref={containerRef} className="relative h-full w-full bg-canvas dark:bg-gray-950">
      <ReactFlow
        nodes={renderNodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        connectionMode={ConnectionMode.Loose}
        onInit={setInstance}
        onConnect={handleConnect}
        onNodesChange={handleNodesChange}
        onNodeClick={handleNodeClick}
        onEdgeClick={handleEdgeClick}
        onPaneClick={handlePaneClick}
        onNodeContextMenu={handleNodeContextMenu}
        onEdgeContextMenu={handleEdgeContextMenu}
        onSelectionChange={handleSelectionChange}
        onNodeDragStart={handleNodeDragStart}
        onNodeDragStop={handleNodeDragStop}
        nodesDraggable={interactionMode === 'select' && !readOnly}
        nodesConnectable={interactionMode === 'select' && !readOnly}
        selectionOnDrag={interactionMode === 'select'}
        selectionKeyCode={interactionMode === 'select' ? 'Shift' : null}
        selectionMode={SelectionMode.Partial}
        panOnDrag={interactionMode === 'select' ? [1, 2] : true}
        panActivationKeyCode="Space"
        minZoom={0.1}
        maxZoom={2}
        onlyRenderVisibleElements={!forceRenderAll}
        elevateEdgesOnSelect
        fitView
      >
        {showGrid && <Background variant={BackgroundVariant.Dots} gap={22} size={1.6} color={theme === 'dark' ? '#27324a' : '#cbd5e1'} />}
        <Controls showInteractive={false} />
        <InteractionModeToggle />
        <ToastPanel />
      </ReactFlow>
      {tourOpen && <TourOverlay />}
      {menu && <ExplanationContextMenu {...menu} documentation={documentation} onClose={() => setMenu(null)} />}
    </div>
  );
}

export default function ArchitectureCanvas({ containerRef }: { containerRef: React.RefObject<HTMLDivElement> }) {
  return (
    <ReactFlowProvider>
      <FlowInner containerRef={containerRef} />
    </ReactFlowProvider>
  );
}
